import { useCallback } from 'react';
import {
  useQuery,
  useSuspenseQuery,
  QueryResult,
  useLazyQuery,
  useApolloClient,
  QueryOptions,
  QueryHookOptions,
  SuspenseQueryHookOptions,
  LazyQueryHookOptions,
  LazyQueryExecFunction
} from '@apollo/client';
import { useSnackbarHelper } from '../../useSnackbarHelper';
import QUERY from '../operations/MarketplaceTracking.graphql';
import { Query, QuerymarketplaceTrackingArgs } from '../../__generated__/globalTypes';

export const useMarketplaceTrackingQuery = (options?: QueryHookOptions<Query, QuerymarketplaceTrackingArgs>) => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const result = useQuery<Query, QuerymarketplaceTrackingArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return result;
};

export const useMarketplaceTrackingLazyQuery = (
  options?: LazyQueryHookOptions<Query, QuerymarketplaceTrackingArgs>
): QueryResult<Query, QuerymarketplaceTrackingArgs> & {
  getMarketplaceTracking: LazyQueryExecFunction<Query, QuerymarketplaceTrackingArgs>;
} => {
  const { t, enqueueSnackbar } = useSnackbarHelper();
  const [getMarketplaceTracking, result] = useLazyQuery<Query, QuerymarketplaceTrackingArgs>(QUERY, {
    onError: (error) => {
      enqueueSnackbar(t(error.message), { variant: 'error' });
    },
    ...options
  });

  return { getMarketplaceTracking, ...result };
};

export const useMarketplaceTrackingPromiseQuery = () => {
  const client = useApolloClient();

  const getMarketplaceTracking = useCallback(
    (options?: Omit<QueryOptions<QuerymarketplaceTrackingArgs, Query>, 'query'>) =>
      client.query<Query, QuerymarketplaceTrackingArgs>({ ...options, query: QUERY }),
    [client]
  );

  return { getMarketplaceTracking };
};
// Suspense Query Hooks
export const useMarketplaceTrackingSuspenseQuery = (
  options?: SuspenseQueryHookOptions<Query, QuerymarketplaceTrackingArgs>
) => {
  const result = useSuspenseQuery<Query, QuerymarketplaceTrackingArgs>(QUERY, {
    ...options
  });

  return result;
};
